import React, { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Plus, Lock } from "lucide-react";
import Segment from "./Segment";
import { useJeMobil } from "../lib/mobil";

/**
 * Jednotný vzhled detailních stránek (plátno 14B): klient, obchodní případ,
 * zařízení. Každá stránka skládá stejné kousky, nikde jinde se neliší:
 *
 *   DetailHlava   — tmavá karta: druh, název, štítky, řádek údajů, dlaždice
 *                   s čísly. Na počítači tlačítka vpravo nahoře (vedlejší,
 *                   hlavní oranžové, „···"), na telefonu kulaté akce s popiskem.
 *   KartaZalozky  — bílá karta se záložkami (oranžová linka) a „+ Přidat"
 *                   vpravo, které se mění podle záložky.
 *   KartaSekce    — bílá karta s nadpisem, počtem a „+ Přidat".
 *   Pridat        — jediné tlačítko „+ něco" (oranžový text) pro všechny karty.
 */

export function Pridat({ text, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="tap md:min-h-0 shrink-0 inline-flex items-center gap-1 t-meta font-700 text-accent hover:text-accent-hover">
      <Plus size={15} strokeWidth={2.4} /> {text}
    </button>
  );
}

/** Menu „···": na počítači rozbalovací, na telefonu panel zespodu. */
function ViceMenu({ polozky, otevreno, setOtevreno, mobil }) {
  const obal = useRef(null);
  useEffect(() => {
    if (!otevreno || mobil) return undefined;
    const klik = (e) => { if (!obal.current?.contains(e.target)) setOtevreno(false); };
    const esc = (e) => { if (e.key === "Escape") setOtevreno(false); };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", klik); document.removeEventListener("keydown", esc); };
  }, [otevreno, mobil, setOtevreno]);
  if (!otevreno) return null;
  const seznam = polozky.map((p) => (
    <button key={p.text} type="button" data-testid={p.testid}
      onClick={() => { setOtevreno(false); p.onClick(); }}
      className={`w-full flex items-center gap-3 px-3 h-12 md:h-10 rounded-[12px] text-left t-telo md:text-[13px] font-600 hover:bg-inputbg ${
        p.nebezpecne ? "text-danger" : "text-ink"}`}>
      {p.ikona && <p.ikona size={16} strokeWidth={1.9} className="shrink-0" />}{p.text}
    </button>
  ));
  if (mobil) {
    return (
      <div className="fixed inset-0 z-50 flex items-end" data-testid="detail-vice-panel">
        <div className="absolute inset-0 bg-black/40" onClick={() => setOtevreno(false)} />
        <div className="okno-in relative w-full bg-white rounded-t-sheet shadow-panel px-3 pt-2 pb-[calc(16px+env(safe-area-inset-bottom))]">
          <span className="block w-10 h-[5px] rounded-full bg-line-strong mx-auto mb-2" />
          {seznam}
        </div>
      </div>
    );
  }
  return (
    <div ref={obal} className="vyber-menu absolute right-0 top-[calc(100%+8px)] z-40 w-[230px] bg-white rounded-card border border-line-softer shadow-panel p-1.5 text-ink"
      data-testid="detail-vice-menu">
      {seznam}
    </div>
  );
}

/**
 * hlavni   = { text, onClick, ikona?, testid }            oranžové tlačítko
 * vedlejsi = [{ text, onClick, ikona?, testid } | { text, prvek }]  průhledná tlačítka vlevo od hlavního
 *            (`prvek` = hotové tlačítko s vlastním chováním, např. náhled PDF s výběrem)
 * vice     = [{ text, onClick, ikona?, nebezpecne?, testid }]  položky menu „···"
 * cta      = [{ text, ikona, onClick | href, testid }]     kulaté akce na telefonu (první je hlavní)
 * dlazdice = [{ popisek, hodnota, jednotka?, pod?, zvyraznit?: "accent"|"danger"|barva, onClick?, testid? }]
 * obrazek = kolečko / logo vlevo od názvu (fotka člena týmu, iniciály partnera)
 */
export default function DetailHlava({ druh, nazev, stitky, meta, pod, hlavni, vedlejsi = [], vice = [], cta = [], dlazdice = [], obrazek, testid }) {
  const [menu, setMenu] = useState(false);
  const mobil = useJeMobil();
  const barva = (z) => (z === "accent" ? "#ffb182" : z === "danger" ? "#ff8a7a" : z || "#ffffff");
  const sloupce = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[dlazdice.length] || "md:grid-cols-4";
  const ctaPolozky = vice.length ? [...cta, { text: "Více", ikona: MoreHorizontal, onClick: () => setMenu(true), testid: "detail-cta-vice" }] : cta;

  return (
    <div className="rounded-[24px] bg-ink text-white p-5 md:p-6 flex flex-col gap-4 md:gap-5" data-testid={testid}>
      <div className="flex items-start gap-3.5 md:gap-4">
        {obrazek && <span className="shrink-0">{obrazek}</span>}
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <span className="label-up text-muted-3">{druh}</span>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[22px] md:text-[28px] font-800 tracking-tight leading-[1.1]">{nazev}</h1>
            {stitky}
          </div>
          {meta && meta.length > 0 && (
            <div className="flex flex-wrap gap-x-[18px] gap-y-1.5 t-meta text-[#a3a7ad]">
              {meta.map((m, i) => <React.Fragment key={i}>{m}</React.Fragment>)}
            </div>
          )}
          {pod}
        </div>
        {/* počítač: tlačítka vpravo nahoře */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {vedlejsi.map((b) => b.prvek ? <React.Fragment key={b.text}>{b.prvek}</React.Fragment> : (
            <button key={b.text} type="button" onClick={b.onClick} data-testid={b.testid} disabled={b.disabled}
              className="pill h-10 px-4 inline-flex items-center gap-1.5 text-[13px] font-700 bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.16)] hover:bg-white/[.16]">
              {b.ikona && <b.ikona size={15} />}{b.text}
            </button>
          ))}
          {hlavni && (
            <button type="button" onClick={hlavni.onClick} data-testid={hlavni.testid} disabled={hlavni.disabled}
              className="pill h-10 px-4 inline-flex items-center gap-1.5 text-[13px] font-700 bg-accent text-white hover:bg-accent-hover disabled:opacity-60">
              {hlavni.ikona && <hlavni.ikona size={15} strokeWidth={2.4} />}{hlavni.text}
            </button>
          )}
          {vice.length > 0 && (
            <span className="relative">
              <button type="button" onClick={() => setMenu((v) => !v)} aria-label="Další akce" aria-expanded={menu}
                data-testid="detail-vice"
                className="h-10 w-10 rounded-full inline-flex items-center justify-center bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.16)] hover:bg-white/[.16]">
                <MoreHorizontal size={18} />
              </button>
              {!mobil && <ViceMenu polozky={vice} otevreno={menu} setOtevreno={setMenu} mobil={false} />}
            </span>
          )}
        </div>
      </div>

      {dlazdice.length > 0 && (
        <div className={`grid grid-cols-2 ${sloupce} gap-2 md:gap-2.5`}>
          {dlazdice.map((d, i) => {
            const Tag = d.onClick ? "button" : "div";
            // Lichý počet: na telefonu poslední dlaždice přes celou šířku, ať nevisí sama v půlce.
            const sama = dlazdice.length % 2 === 1 && i === dlazdice.length - 1;
            return (
              <Tag key={d.popisek} type={d.onClick ? "button" : undefined} onClick={d.onClick} data-testid={d.testid}
                className={`min-w-0 text-left rounded-[16px] bg-white/[.06] px-3.5 md:px-[18px] py-3 md:py-4 ${sama ? "col-span-2 md:col-span-1" : ""} ${d.onClick ? "hover:bg-white/10" : ""}`}>
                <div className="label-up text-muted-3 truncate">{d.popisek}</div>
                <div className="amount mt-1.5 text-[17px] md:text-[24px] font-800 tracking-tight truncate" style={{ color: barva(d.zvyraznit) }}>
                  {d.hodnota}{d.jednotka && <span className="text-[12px] md:text-[14px] text-muted-3 font-700"> {d.jednotka}</span>}
                </div>
                {d.pod && <div className="hidden md:block mt-0.5 t-meta text-muted-3 truncate">{d.pod}</div>}
              </Tag>
            );
          })}
        </div>
      )}

      {/* telefon: kulaté akce s popiskem (první je hlavní) */}
      {ctaPolozky.length > 0 && (
        <div className="md:hidden flex justify-around -mx-1 pt-0.5" data-testid="detail-cta">
          {ctaPolozky.map((c, i) => {
            const vnitrek = (
              <>
                <span className={`h-12 w-12 rounded-full inline-flex items-center justify-center ${
                  i === 0 ? "bg-accent" : "bg-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,.14)]"}`}>
                  <c.ikona size={19} strokeWidth={2} />
                </span>
                <span className={`text-[11.5px] font-600 ${i === 0 ? "text-white" : "text-[#b9bdc3]"}`}>{c.text}</span>
              </>
            );
            const cls = "w-[70px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform";
            return c.href
              ? <a key={c.text} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={cls} data-testid={c.testid}>{vnitrek}</a>
              : <button key={c.text} type="button" onClick={c.onClick} className={cls} data-testid={c.testid}>{vnitrek}</button>;
          })}
          {mobil && <ViceMenu polozky={vice} otevreno={menu} setOtevreno={setMenu} mobil />}
        </div>
      )}
    </div>
  );
}

/** Bílá karta se záložkami. zalozky = [{ key, label, pocet, testid, zamceno? }].
 *  `zamceno` = důvod, proč záložka ještě nejde otevřít (zámek + důvod při najetí). */
export function KartaZalozky({ zalozky, aktivni, onZmena, akce, children, testid }) {
  return (
    <div className="card overflow-hidden min-w-0" data-testid={testid}>
      <div className="px-4 md:px-5 border-b border-line flex items-center gap-4">
        <Segment varianta="podtrzeni" className="flex gap-5 md:gap-6 overflow-x-auto [scrollbar-width:none]">
          {zalozky.map((t) => (
            <button type="button" data-on={aktivni === t.key} key={t.key} onClick={() => !t.zamceno && onZmena(t.key)} data-testid={t.testid || `tab-${t.key}`}
              title={t.zamceno || undefined} aria-disabled={t.zamceno ? "true" : undefined}
              className={`h-12 px-0.5 t-telo md:text-[13.5px] whitespace-nowrap inline-flex items-center gap-1 ${
                aktivni === t.key ? "text-ink font-700" : t.zamceno ? "text-muted-2 font-600 cursor-not-allowed" : "text-sec-3 font-600 hover:text-ink"}`}>
              {t.zamceno && <Lock size={12} />}{t.label}{t.pocet != null && <span className={`amount ml-1 ${aktivni === t.key ? "text-muted" : "text-muted-2"}`}>{t.pocet}</span>}
            </button>
          ))}
        </Segment>
        <span className="ml-auto">{akce}</span>
      </div>
      {children}
    </div>
  );
}

/** Bílá karta s nadpisem, počtem a akcí vpravo. */
export function KartaSekce({ titul, pocet, akce, children, testid, telo = false }) {
  return (
    <div className="card overflow-hidden min-w-0" data-testid={testid}>
      <div className="px-4 md:px-5 py-3.5 flex items-center gap-2">
        <h2 className="t-sekce text-ink font-700">{titul}</h2>
        {pocet != null && <span className="amount t-meta text-muted font-600">{pocet}</span>}
        <span className="ml-auto">{akce}</span>
      </div>
      {telo ? <div className="px-4 md:px-5 pb-4">{children}</div> : children}
    </div>
  );
}

/** Štítek do tmavé hlavičky (průhledný). */
export function StitekTmavy({ children, barva = "#d7dade", testid }) {
  return (
    <span data-testid={testid} className="label-up px-2.5 py-1 rounded-pill shrink-0 bg-white/10" style={{ color: barva }}>{children}</span>
  );
}
